import {
  Battle_Action_Dance,
  Battle_Action_Faint,
  Battle_Action_Move,
  Battle_Action_Turn,
  Battle_Action_UseSkill,
  BattlePokemonData,
  Direction,
  Position,
  SkillStatusEffect,
  SkillType,
  StatusEffect,
} from "types/battle-engine/battle";
import {
  AnimationName,
  VALID_ANIMATIONS,
} from "types/battle-engine/animations";
import {
  createFakeRotationSheet,
  loadAnimXML,
  makeSprite,
  SheetSlice,
  slice8DirSheet,
} from "@/utils/sprites/sheet-cut";
import { parsePokeNameForSprite } from "utils/pokemon/name-utils";
import {
  AnimatedSprite,
  Application,
  Assets,
  Cache,
  ColorMatrix,
  ColorMatrixFilter,
  Container,
  EventEmitter,
  Filter,
  Graphics,
  Sprite,
  Text,
  Texture,
} from "pixi.js";
import { vectorToDir } from "../utils/vector-to-dir";
import {
  gridMarginX,
  gridMarginY,
  gridTileSize,
  MS_PER_TICK,
} from "types/battle-engine/constants";
import { Pokemon } from "./pokemon";
import { ClientPokemonSkill } from "./client-pokemon-skill";
import { S3_BASE_URL } from "@/utils/s3";
import { isArray } from "lib/tool";
import { getPokemonSpriteScale } from "game/data/pokemon/sprite-scale-overrides";
import { VFX } from "./client-vfx";
import { VFX_SPRITE_FRAME_COUNT_DATA } from "@/data/vfx-sprite-frame-count-data";

Assets.setPreferences({
  crossOrigin: "anonymous",
  preferCreateImageBitmap: false,
});

const ANIMATION_SPEED = 1.33;

export class ClientPokemon extends Pokemon {
  public isCharging = false;
  public events = new EventEmitter();



  // UI fields
  public container: Container;
  protected nameText!: Text;
  protected hpBg!: Sprite;
  protected hpFill!: Graphics;
  protected shieldFill!: Graphics;
  protected statusEffectContainer!: Container;
  protected statusEffectSprites: Map<
    StatusEffect,
    {
      container: Container;
      sprite: Sprite;
      intensityText: Text | null;
      startTime: number;
      lastIntensity: number;
      lastDuration: number;
      isBumping: boolean;
    }
  > = new Map();

  protected currentDir: Direction = Direction.SE;
  protected currentAnimName: string = "Idle";
  protected whiteFlash = new ColorMatrixFilter();
  protected isHurting = false;
  protected currentTargetId: number | null = null;
  protected isFlashing = false; // Prevent concurrent white flashes

  protected animations: Record<string, SheetSlice> = {};
  protected shadowAnimations: Record<string, SheetSlice> = {};
  protected animatedSprite: AnimatedSprite | null = null;
  protected animatedShadowSprite: AnimatedSprite | null = null;
  protected team: "A" | "B";
  protected app: Application;
  protected parent: Container;

  protected specialMeleeAnimations: string[] = [];
  protected specialRangedAnimations: string[] = [];
  protected maxShieldAmount: number = 0; // Track max shield for bar scaling

  // Premier aura fields
  protected premierAuraSprites: AnimatedSprite[] = [];
  protected premierAuraFilters: ColorMatrixFilter[] = [];
  protected premierAuraAnimationFrame: number | null = null;
  protected premierAuraColors: number[] = []; // Default: gold, white, pink
  protected premierAuraSpeed: number = 150; // ms per color change
  protected premierAuraStrokeWidth: number = 1.5; // pixels

  protected override skills: ClientPokemonSkill[] = [];
  protected override enemies: ClientPokemon[] = [];

  // Knockup animation state
  protected knockupAnimationFrame: number | null = null;
  protected isKnockedUp: boolean = false;
  protected knockupBaseY: number = 0;

  // Movement state
  protected movingDir: Direction = Direction.SE;
  protected ongoingMovementActions: number = 0;
  protected get isMoving(): boolean {
    return this.ongoingMovementActions > 0;
  }

  protected hideUI: boolean;
  protected hideHPBar: boolean;

  // Smooth rotation state
  protected isRotating = false;
  protected rotationQueue: Array<{
    animationOptions: string[];
    dir: Direction;
    frame?: number;
  }> = [];
  protected rotationStartTime = 0;
  protected rotationDuration = 150; // ms
  protected rotationPath: Direction[] = [];
  protected rotationOnComplete: (() => void) | null = null;

  // Interaction
  protected onClickHandler: ((pokemon: ClientPokemon) => void) | null = null;

  // Visual Status Logic (Draft Phase)
  protected visualStatusText: Text | null = null;
  protected visualStatusGlow: Graphics | null = null;
  protected visualStatusArrow: Text | null = null;
  protected visualStatusAnimationFrame: number | null = null;
  protected visualStatusArrowAnimFrame: number | null = null;

  constructor(
    pokeData: BattlePokemonData,
    initialPosition: Position,
    team: "A" | "B",
    app: Application,
    parent?: Container,
    hideUI: boolean = false,
    hideHPBar: boolean = false,
  ) {
    super(pokeData);

    this.pokemonData = pokeData;
    this.team = team;
    this.app = app;
    this.parent = parent ?? app.stage;
    this.hideUI = hideUI;
    this.hideHPBar = hideHPBar;

    this.container = new Container();
    this.container.pivot.set(0.5);
    this.container.x =
      initialPosition.x * gridTileSize + gridMarginX + gridTileSize / 2;
    this.container.y =
      initialPosition.y * gridTileSize + gridMarginY + gridTileSize / 2;

    // Enable interaction
    this.container.eventMode = "static";
    this.container.cursor = "pointer";
    this.container.on("pointerdown", () => {
      if (this.onClickHandler) {
        this.onClickHandler(this);
      }
    });

    this.skills = pokeData.skills.map(
      (skill) => new ClientPokemonSkill(skill, this, app, parent),
    );

    console.log(
      `[ClientPokemon constructor] [${this.base_pokemon_name}] -> HP: ${this.hp.current} / ${this.hp.max}`,
    );
  }

  public setVisualStatus(
    status: "idle" | "selected" | "picked" | "banned",
    color: number, // Hex color for glow/text
  ) {
    if (!this.container || this.container.destroyed) return;

    // Ensure it's visible and reset alpha before applying status-specific changes
    this.container.visible = true;
    this.container.alpha = status === "banned" ? 0.35 : 1;

    // 0. Handle Banned Status (Play Sleep animation)
    if (status === "banned") {
      this.stopVisualStatusAnimation();
      // Force Direction.S (South) for Sleep animation to ensure the face is visible
      // and avoid missing back-facing sleep sprites
      this.setAnimation(["Sleep", "Idle"], Direction.S);

      // Reset other visual status elements
      if (this.visualStatusGlow) this.visualStatusGlow.visible = false;
      if (this.visualStatusText) this.visualStatusText.visible = false;
      this.container.filters = [];
      return;
    }

    // 1. Handle Glow/Highlight
    if (status !== "idle") {
      if (!this.visualStatusGlow) {
        this.visualStatusGlow = new Graphics();
        // Insert at bottom (index 0) so it's behind pokemon
        this.container.addChildAt(this.visualStatusGlow, 0);
      }

      this.visualStatusGlow.visible = true;
      this.startVisualStatusAnimation(status as "selected" | "picked", color);

      // Show arrow only for "selected" status
      if (status === "selected") {
        this.showSelectionArrow(color);
      } else {
        this.hideSelectionArrow();
      }
    } else {
      if (this.visualStatusGlow) {
        this.visualStatusGlow.visible = false;
      }
      this.stopVisualStatusAnimation();
      this.hideSelectionArrow();
    }

    // 2. Handle Text Label (PICKED)
    if (status === "picked") {
      const labelText = status.toUpperCase();

      if (!this.visualStatusText) {
        this.visualStatusText = new Text({
          text: "",
          style: {
            fontFamily: "vck",
            fontSize: 16,
            fill: 0xffffff,
            align: "center",
            stroke: { color: 0xffffff, width: 6, join: "round" },
            dropShadow: {
              color: 0x000000,
              blur: 2,
              angle: Math.PI / 4,
              distance: 2,
            },
          },
        });
        this.visualStatusText.anchor.set(0.5);
        this.visualStatusText.y = 30; // Position at feet
        this.visualStatusText.x = 1;
        this.visualStatusText.zIndex = 100;
        this.container.addChild(this.visualStatusText);
      }

      this.visualStatusText.text = labelText;
      this.visualStatusText.style.fill = color; // Tint text with status color
      this.visualStatusText.visible = true;
    } else {
      if (this.visualStatusText) {
        this.visualStatusText.visible = false;
      }
    }

    // 3. Clear Visual Filters (Banned filter removed since we hide it)
    this.container.filters = [];
  }

  protected showSelectionArrow(color: number) {
    if (!this.visualStatusArrow) {
      this.visualStatusArrow = new Text({
        text: "▼",
        style: {
          fontFamily: "vck",
          fontSize: 24,
          fill: color,
          align: "center",
          stroke: { color: 0x000000, width: 4, join: "round" },
          dropShadow: {
            color: 0x000000,
            blur: 2,
            angle: Math.PI / 2,
            distance: 2,
          },
        },
      });
      this.visualStatusArrow.anchor.set(0.5, 1);
      this.visualStatusArrow.zIndex = 2000; // Ensure it's on top
      this.container.addChild(this.visualStatusArrow);
    }

    this.visualStatusArrow.style.fill = color;
    this.visualStatusArrow.visible = true;

    // Position above head
    const spriteH = this.animatedSprite ? this.animatedSprite.height : 60;
    // Base Y is top of sprite
    const baseY = -spriteH / 2 - 40; // Extra padding

    // Start bob animation
    this.startArrowAnimation(baseY);
  }

  protected hideSelectionArrow() {
    if (this.visualStatusArrow) {
      this.visualStatusArrow.visible = false;
    }
    if (this.visualStatusArrowAnimFrame !== null) {
      cancelAnimationFrame(this.visualStatusArrowAnimFrame);
      this.visualStatusArrowAnimFrame = null;
    }
  }

  protected startArrowAnimation(baseY: number) {
    const startTime = performance.now();
    const bobHeight = 6;
    const speed = 10;

    const animate = () => {
      if (!this.visualStatusArrow || !this.visualStatusArrow.visible) {
        this.app.ticker.remove(animate);
        return;
      }

      const now = performance.now();
      const elapsed = (now - startTime) / 1000;

      // Bob up and down using sine wave
      const offset = Math.sin(elapsed * speed) * bobHeight;
      this.visualStatusArrow.y = baseY + offset;
    };

    this.app.ticker.add(animate);
  }

  /** Same as setVisualStatus but without the glow circle (for draft where platform shows color) */
  public setVisualStatusNoGlow(
    status: "idle" | "selected" | "picked" | "banned",
    color: number,
  ) {
    if (!this.container || this.container.destroyed) return;

    this.container.visible = true;
    this.container.alpha = status === "banned" ? 0.35 : 1;

    // Handle Banned Status (Play Sleep animation)
    if (status === "banned") {
      this.stopVisualStatusAnimation();
      this.setAnimation(["Sleep", "Idle"], Direction.S);
      if (this.visualStatusGlow) this.visualStatusGlow.visible = false;
      if (this.visualStatusText) this.visualStatusText.visible = false;
      this.container.filters = [];
      return;
    }

    // Hide glow (platform handles color indication)
    if (this.visualStatusGlow) this.visualStatusGlow.visible = false;
    this.stopVisualStatusAnimation();

    // Handle Text Label (PICKED)
    if (status === "picked") {
      if (!this.visualStatusText) {
        this.visualStatusText = new Text({
          text: "",
          style: {
            fontFamily: "vck",
            fontSize: 16,
            fill: 0xffffff,
            align: "center",
            stroke: { color: 0xffffff, width: 6, join: "round" },
            dropShadow: {
              color: 0x000000,
              blur: 2,
              angle: Math.PI / 4,
              distance: 2,
            },
          },
        });
        this.visualStatusText.anchor.set(0.5);
        this.visualStatusText.y = 30;
        this.visualStatusText.zIndex = 100;
        this.container.addChild(this.visualStatusText);
      }
      this.visualStatusText.text = "PICKED";
      this.visualStatusText.style.fill = color;
      this.visualStatusText.visible = true;
    } else {
      if (this.visualStatusText) this.visualStatusText.visible = false;
    }

    this.container.filters = [];
  }

  protected stopVisualStatusAnimation() {
    if (this.visualStatusAnimationFrame !== null) {
      cancelAnimationFrame(this.visualStatusAnimationFrame);
      this.visualStatusAnimationFrame = null;
    }
  }

  protected startVisualStatusAnimation(
    status: "selected" | "picked",
    color: number,
  ) {
    if (!this.visualStatusGlow) return;

    const startTime = performance.now();

    const animate = () => {
      if (
        !this.visualStatusGlow ||
        this.visualStatusGlow.destroyed ||
        !this.visualStatusGlow.visible
      ) {
        this.app.ticker.remove(animate);
        return;
      }

      const now = performance.now();
      const elapsed = (now - startTime) / 1000;

      this.visualStatusGlow.clear();

      const glowWidth = gridTileSize * 0.65;
      const glowHeight = gridTileSize * 0.325;

      // Base ring
      this.visualStatusGlow.ellipse(0, 0, glowWidth, glowHeight);
      this.visualStatusGlow.fill({ color, alpha: 0.45 });
      this.visualStatusGlow.stroke({ width: 2, color, alpha: 0.45 });

      // Animated rings
      if (status === "selected") {
        // Pulsing selection
        const pulse = 1 + Math.sin(elapsed * 5) * 0.1;
        this.visualStatusGlow.ellipse(
          0,
          0,
          glowWidth * pulse,
          glowHeight * pulse,
        );
        this.visualStatusGlow.stroke({ width: 1.5, color, alpha: 0.4 });
      } else if (status === "picked") {
        // Rotating/Radiating effect for picked
        const pulse = 1 + Math.sin(elapsed * 3) * 0.05;

        // Inner intense ring - FILLED
        this.visualStatusGlow.ellipse(0, 0, glowWidth * 0.8, glowHeight * 0.8);
        this.visualStatusGlow.fill({ color, alpha: 0.4 });
        this.visualStatusGlow.stroke({ width: 2, color, alpha: 0.8 });

        // Outer pulsing ring
        this.visualStatusGlow.ellipse(
          0,
          0,
          glowWidth * 1.2 * pulse,
          glowHeight * 1.2 * pulse,
        );
        this.visualStatusGlow.stroke({ width: 1, color, alpha: 0.3 });
      }
    };

    this.app.ticker.add(animate);
  }

  /**
   * Updates the pokemon's battle data in-place from battle log data.
   * Used for scene continuity: draft pokemon are updated with real battle stats
   * without recreating sprites or animations.
   *
   * @param newData - The real BattlePokemonData from battle log
   * @param newPosition - The battle grid position (in grid units, not pixels)
   * @param team - Update team if provided (optional)
   */
  public updateBattleData(
    newData: BattlePokemonData,
    newPosition: Position,
    team?: "A" | "B",
  ): void {
    // Update identity
    this.id = newData.id;
    this.name = newData.name;
    this.base_pokemon_name = newData.base_pokemon_name;
    this.pokemonData = newData;
    if (team) {
      this.team = team;
    }

    // Clear visual status artifacts from draft phase
    this.stopVisualStatusAnimation();
    this.hideSelectionArrow();
    if (this.visualStatusGlow) {
      this.visualStatusGlow.destroy();
      this.visualStatusGlow = null;
    }
    if (this.visualStatusArrow) {
      this.visualStatusArrow.destroy();
      this.visualStatusArrow = null;
    }
    if (this.visualStatusText) {
      this.visualStatusText.destroy();
      this.visualStatusText = null;
    }
    this.container.filters = [];

    // Update stats
    this.hp = { current: newData.hp, max: newData.max_hp };
    this.ability_power = newData.ability_power;
    this.resistance = newData.resistance;
    this.speed = newData.speed ?? 100;

    // Update skills with new ClientPokemonSkill instances
    this.skills = newData.skills.map(
      (skill) => new ClientPokemonSkill(skill, this, this.app, this.parent),
    );

    // Update position (convert grid units to pixel coordinates)
    this.container.x =
      newPosition.x * gridTileSize + gridMarginX + gridTileSize / 2;
    this.container.y =
      newPosition.y * gridTileSize + gridMarginY + gridTileSize / 2;
    this.container.zIndex = this.container.y;
    this.position = {
      x: this.container.x,
      y: this.container.y,
    };

    // Reset combat state
    this.shield = { amount: 0, durationTicks: 0 };
    this.activeStatusEffects.clear();
    this.isHurting = false;
    this.isFlashing = false;
    this.currentTargetId = null;

    // Update HP bar visual
    this.setHP(this.hp.current);

    console.log(
      `[ClientPokemon.updateBattleData] Updated ${this.base_pokemon_name} (id: ${this.id}) at grid (${newPosition.x}, ${newPosition.y})`,
    );
  }

  /**
   * Clears draft phase visual artifacts (PICKED text, glow) and shows HP bar.
   * Called when transitioning from draft to battle.
   */
  public transitionToBattle(): void {
    // Clear visual status artifacts from draft phase
    this.stopVisualStatusAnimation();
    this.hideSelectionArrow();
    if (this.visualStatusGlow) {
      this.visualStatusGlow.destroy();
      this.visualStatusGlow = null;
    }
    if (this.visualStatusArrow) {
      this.visualStatusArrow.destroy();
      this.visualStatusArrow = null;
    }
    if (this.visualStatusText) {
      this.visualStatusText.destroy();
      this.visualStatusText = null;
    }
    this.container.filters = [];
    this.container.alpha = 1;

    // Show HP bar if it was hidden
    if (this.hideHPBar) {
      this.hideHPBar = false;
      this.createHPBar();
    }
  }

  /**
   * Creates HP bar UI elements. Called during load() or when transitioning to battle.
   */
  protected async createHPBar(): Promise<void> {
    if (this.hpBg) return; // Already exists

    const hpBgUrl = `/assets/ui/hp_bg.png`;
    const hpBgTex = await Assets.load<Texture>(hpBgUrl);
    this.hpBg = Sprite.from(hpBgTex);
    this.hpBg.scale.set(0.5);

    // Team-based health bar colors: Blue for team A (Player), Red for team B (Enemy)
    const teamColor = this.team === "A" ? 0x3366ff : 0xff3333;

    this.hpFill = new Graphics().rect(-8, -5, 22, 7).fill({ color: teamColor });
    this.hpBg.anchor.set(0.5, 1);

    this.container.addChild(this.hpBg, this.hpFill);

    this.shieldFill = new Graphics()
      .rect(-2, 7, 22, 7)
      .fill({ color: 0xffffff, alpha: 1 });
    this.shieldFill.pivot.set(0, 0);
    this.shieldFill.visible = false;
    this.container.addChild(this.shieldFill);

    // Status effect icons container
    this.statusEffectContainer = new Container();
    this.container.addChild(this.statusEffectContainer);

    // Layout and set initial HP
    this.layoutUI(true);
    this.setHP(this.hp.current);
  }

  async load() {
    let parsedPokemonName = parsePokeNameForSprite(
      this.pokemonData.base_pokemon_name,
    );

    if (this.pokemonData.is_shiny) {
      parsedPokemonName += "__shiny";
    }

    // Load XML once
    const animSpecs = await loadAnimXML(
      `${S3_BASE_URL}/pokemons/${parsedPokemonName}/AnimData.xml`,
    );

    if (this.base_pokemon_name === "lileep") console.log(animSpecs);

    let idleAnim: SheetSlice | null = null;
    let idleShadowAnim: SheetSlice | null = null;

    const animLoadPromises: { slice: Promise<SheetSlice>; name: string }[] = [];
    const shadowAnimLoadPromises: {
      slice: Promise<SheetSlice>;
      name: string;
    }[] = [];

    const invalidAnims = ["Double", "Twirl"];
    const v = Date.now();

    for (const [animName, newAnim] of Object.entries(animSpecs)) {
      const newAnimName = newAnim.name;

      if (invalidAnims.includes(newAnimName)) continue;

      // Only load animations that are defined in our AnimationName enum
      if (!VALID_ANIMATIONS.includes(newAnimName as AnimationName)) continue;

      if (newAnimName === "Rotate") {
        const idleAnimSheet = await Assets.load<Texture>({
          src: `${S3_BASE_URL}/pokemons/${parsedPokemonName}/Idle-Anim.png?v=${v}`,
          data: {
            src: `${S3_BASE_URL}/pokemons/${parsedPokemonName}/Idle-Anim.png?v=${v}`,
            resourceOptions: { crossorigin: "anonymous" },
            loadOptions: {
              fetch: { mode: "cors", credentials: "omit", cache: "reload" },
            },
          },
        });

        const idleShadowAnimSheet = await Assets.load<Texture>({
          src: `${S3_BASE_URL}/pokemons/${parsedPokemonName}/Idle-Shadow.png?v=${v}`,
          data: {
            resourceOptions: { crossorigin: "anonymous" },
            loadOptions: {
              fetch: { mode: "cors", credentials: "omit", cache: "reload" },
            },
          },
        });

        const idleAnimInfo = animSpecs["Idle"];

        const rotateAnimSlice = createFakeRotationSheet(
          idleAnimSheet,
          idleAnimInfo?.frameWidth ?? newAnim.frameWidth,
          idleAnimInfo?.frameHeight ?? newAnim.frameHeight,
        );
        const rotateShadowAnimSlice = createFakeRotationSheet(
          idleShadowAnimSheet,
          idleAnimInfo?.frameWidth ?? newAnim.frameWidth,
          idleAnimInfo?.frameHeight ?? newAnim.frameHeight,
        );

        animLoadPromises.push({
          slice: rotateAnimSlice,
          name: "Rotate",
        });
        shadowAnimLoadPromises.push({
          slice: rotateShadowAnimSlice,
          name: "Rotate",
        });
        continue;
      }

      // Map animation name -> image path (change to your convention)
      const animSheetURL = `${S3_BASE_URL}/pokemons/${parsedPokemonName}/${newAnimName}-Anim.png?v=${v}`;
      const shadowAnimSheetURL = `${S3_BASE_URL}/pokemons/${parsedPokemonName}/${newAnimName}-Shadow.png?v=${v}`;

      let animSheet: Texture | null = null;
      let shadowAnimSheet: Texture | null = null;
      try {
        animSheet = await Assets.load<Texture>({
          src: animSheetURL,
          data: {
            resourceOptions: { crossorigin: "anonymous" },
            loadOptions: {
              fetch: { mode: "cors", credentials: "omit", cache: "reload" },
            },
          },
        });

        shadowAnimSheet = await Assets.load<Texture>({
          src: shadowAnimSheetURL,
          data: {
            resourceOptions: { crossorigin: "anonymous" },
            loadOptions: {
              fetch: { mode: "cors", credentials: "omit", cache: "reload" },
            },
          },
        });
      } catch (error) {
        continue;
      }

      if (!animSheet || !shadowAnimSheet) continue;

      const newAnimSlice = slice8DirSheet(
        animSheet,
        newAnim.frameWidth,
        newAnim.frameHeight,
        newAnim.durations,
        20, // each XML "tick" = 16ms (~60fps). Adjust if needed.
        newAnim,
      );
      const newShadowAnimSlice = slice8DirSheet(
        shadowAnimSheet,
        newAnim.frameWidth,
        newAnim.frameHeight,
        newAnim.durations,
        20, // each XML "tick" = 16ms (~60fps). Adjust if needed.
        newAnim,
      );

      shadowAnimLoadPromises.push({
        slice: newShadowAnimSlice,
        name: newAnimName,
      });
      animLoadPromises.push({ slice: newAnimSlice, name: newAnimName });
    }

    const animSlices = await Promise.all(
      animLoadPromises.map(async (p) => ({
        name: p.name,
        slice: await p.slice,
      })),
    );
    const shadowAnimSlices = await Promise.all(
      shadowAnimLoadPromises.map(async (p) => ({
        name: p.name,
        slice: await p.slice,
      })),
    );

    const availableAnimations: string[] = [];

    for (let i = 0; i < animSlices.length; i++) {
      const slice = animSlices[i];
      if (!slice) continue;

      const animationName = slice.name;
      if (!animationName) throw new Error("Animation name is required");

      const animSlice = slice;
      if (!animSlice) throw new Error("Animation slice is required");

      const shadowAnimSlice = shadowAnimSlices.find(
        (s) => s.name === animationName,
      )?.slice;
      if (!shadowAnimSlice)
        throw new Error("Shadow animation slice is required");

      // If the animation has no frames, skip it. Do not count as existing.
      if (animSlice.slice.cols === 0) continue;

      this.animations[animationName] = animSlice.slice;
      this.shadowAnimations[animationName] = shadowAnimSlice;

      if (animationName === "Idle") {
        idleAnim = animSlice.slice;
        idleShadowAnim = shadowAnimSlice;
      }

      availableAnimations.push(animationName);
    }

    this.specialMeleeAnimations = availableAnimations.filter((anim) =>
      [
        AnimationName.Slice,
        AnimationName.Punch,
        AnimationName.Kick,
        AnimationName.MultiScratch,
        AnimationName.Strike,
        AnimationName.MultiStrike,
        AnimationName.Stomp,
        AnimationName.Bite,
        AnimationName.Uppercut,
        AnimationName.TailWhip,
        AnimationName.Scratch,
        AnimationName.Slam,
        AnimationName.Jab,
      ].includes(anim as AnimationName),
    );

    this.specialRangedAnimations = availableAnimations.filter((anim) =>
      [
        AnimationName.Shoot,
        AnimationName.SpAttack,
        AnimationName.Appeal,
        AnimationName.Roar,
        AnimationName.Sing,
        AnimationName.Sound,
        AnimationName.Shock,
        AnimationName.Emit,
      ].includes(anim as AnimationName),
    );

    if (!idleAnim)
      idleAnim =
        this.animations[
        Object.keys(this.animations)[0] as keyof typeof this.animations
        ] || null;

    // Start facing set
    // 1) Animated sprite (centered in the container)
    const animatedSprite = makeSprite(idleAnim!.byDir[Direction.SE] || []);
    animatedSprite.anchor.set(0.5);
    animatedSprite.x = 0;
    animatedSprite.y = 0;
    const configScale = getPokemonSpriteScale(
      this.pokemonData.base_pokemon_name,
    );
    const runtimeScale = this.pokemonData.scale ?? 1;
    animatedSprite.scale = 2.75 * configScale * runtimeScale; // pixel art: keep integer scales
    animatedSprite.animationSpeed = ANIMATION_SPEED;
    animatedSprite.play();

    this.animatedSprite = animatedSprite;

    // Create shadow animated sprite
    const shadowAnimatedSprite = makeSprite(
      idleShadowAnim!.byDir[Direction.SE] || [],
    );
    shadowAnimatedSprite.anchor.set(0.5);
    shadowAnimatedSprite.x = 0;
    shadowAnimatedSprite.y = 0;
    shadowAnimatedSprite.scale = 2 * configScale * runtimeScale; // pixel art: keep integer scales
    shadowAnimatedSprite.animationSpeed = ANIMATION_SPEED;
    shadowAnimatedSprite.play();
    this.animatedShadowSprite = shadowAnimatedSprite;

    // Add shadow first (renders below), then sprite
    this.container.addChild(shadowAnimatedSprite);
    this.container.addChild(animatedSprite);

    const nameText =
      this.pokemonData.name ?? this.pokemonData.base_pokemon_name;
    const capitalizedName = nameText
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    // 2) Name (above head)
    if (!this.hideUI) {
      this.nameText = new Text({
        text: capitalizedName,
        style: {
          fontFamily: "vck",
          fontSize: 12,
          fill: "0xffffff",
          align: "center",
          stroke: {
            color: 0x000000, // black outline
            width: 6, // thickness in pixels
            join: "round", // miter | round | bevel
          },
        },
      });
      this.nameText.anchor.set(0.5, 1); // center, bottom-align
      this.container.addChild(this.nameText);

      // 3) HP bar (two images: bg + fill). Position just above the name.
      // Skip HP bar if hideHPBar is true (e.g. during draft phase)
      if (!this.hideHPBar) {
        const hpBgUrl = `/assets/ui/hp_bg.png`;

        const hpBgTex = await Assets.load<Texture>(hpBgUrl);
        this.hpBg = Sprite.from(hpBgTex);
        this.hpBg.scale.set(0.5);

        // Team-based health bar colors: Blue for team A (Player), Red for team B (Enemy)
        const teamColor = this.team === "A" ? 0x3366ff : 0xff3333;

        this.hpFill = new Graphics()
          .rect(-4, -3, 22, 6) // x, y, w, h
          .fill({ color: teamColor });

        // centralizar ponto de ancoragem se quiser
        this.hpFill.pivot.set(0, 0);
        this.hpBg.anchor.set(0.5, 1);

        this.container.addChild(this.hpBg, this.hpFill);

        this.shieldFill = new Graphics()
          .rect(-4, 7, 22, 7)
          .fill({ color: 0xffffff, alpha: 1 });
        this.shieldFill.pivot.set(0, 0);
        this.shieldFill.visible = false;

        this.container.addChild(this.shieldFill);

        // 4) Status effect icons container
        this.statusEffectContainer = new Container();
        this.container.addChild(this.statusEffectContainer);
      }

      // 5) Layout once (you can re-call this on animation changes if needed)
      this.layoutUI(true);
    }

    // 5) Initialize HP
    this.setHP(this.hp.current);

    this.parent.addChild(this.container);

    // 6) Start premier aura if enabled
    if (this.pokemonData.premier_aura?.active) {
      this.premierAuraColors = this.pokemonData.premier_aura.colors;
      this.startPremierAura();
    }
  }


  public setOnClick(handler: (pokemon: ClientPokemon) => void) {
    this.onClickHandler = handler;
  }

  public sleep(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
  }

  protected layoutUI(setY: boolean = true) {
    // Guard against destroyed name text
    if (!this.nameText || this.nameText.destroyed) return;

    const spriteH = this.animatedSprite ? this.animatedSprite.height : 32;
    const margin = 0;

    // Start from top of sprite and stack upwards
    // spriteH is the full height. Anchor is 0.5, so top is -height/2.
    let currentY = -spriteH / 2 - margin;

    // 1. HP Bar (closest to sprite)
    if (
      this.hpBg &&
      this.hpFill &&
      !this.hpBg.destroyed &&
      !this.hpFill.destroyed
    ) {
      // Position HP bar
      this.hpBg.x = 0;
      this.hpBg.y = setY ? currentY : this.hpBg.y;

      // Adjust fill position
      // Rect is (-2, -2, 22, 7). To center 22px width at x=0, we need visual left at -11.
      // x + (-2) = -11 => x = -9.
      const fillX = -9;

      this.hpFill.x = fillX;
      // Keep vertical relationship: Fill is slightly above BG bottom anchor
      this.hpFill.y = setY ? this.hpBg.y - 10 : this.hpFill.y;

      // Shield bar
      if (this.shieldFill && !this.shieldFill.destroyed) {
        this.shieldFill.x = fillX;
        this.shieldFill.y = setY ? this.hpBg.y - 10 : this.shieldFill.y;
      }

      // Status effect icons container
      if (this.statusEffectContainer) {
        this.statusEffectContainer.x = 0; // Centered
        // Position status effects above HP bar
        this.statusEffectContainer.y = setY
          ? this.hpBg.y + 5
          : this.statusEffectContainer.y;
      }

      // Move currentY up for the next element (Name)
      // HP Bar is roughly 10-15px high + some padding
      currentY -= 20;
    }

    // 2. Pokemon Name
    this.nameText.x = 0;
    this.nameText.y = setY ? currentY : this.nameText.y;
  }

  /**
   * Update status effect icons display
   */
  protected updateStatusEffectIcons(): void {
    if (!this.statusEffectContainer) return;

    const iconSize = 32;
    const iconSpacing = -4;
    let xOffset = 0;

    // Track which status effects are new or changed
    const currentStatuses = new Set(this.ActiveStatusEffects.keys());
    const previousStatuses = new Set(this.statusEffectSprites.keys());

    // Simple heuristic: if counts differ or any key/intensity/duration changed
    let hasChanges = false;
    const changesIn = new Set<StatusEffect>();

    // Remove status effects that are no longer active
    for (const status of previousStatuses) {
      if (!currentStatuses.has(status)) {
        const spriteData = this.statusEffectSprites.get(status);
        if (spriteData) {
          this.statusEffectContainer.removeChild(spriteData.container);
        }
        this.statusEffectSprites.delete(status);
      }
    }

    for (const [status, effect] of this.ActiveStatusEffects.entries()) {
      const existing = this.statusEffectSprites.get(status);
      if (!existing || existing.lastIntensity !== effect.intensity) {
        hasChanges = true;
        changesIn.add(status);
      }
    }

    if (!hasChanges) return;

    const currentStatusKeys = new Set(this.ActiveStatusEffects.keys());

    // Display status effects as sprite icons
    for (const [status, effect] of this.ActiveStatusEffects) {
      const existingSprite = this.statusEffectSprites.get(status);

      if (existingSprite) {
        // Update position for existing icon
        existingSprite.container.x =
          xOffset -
          (this.ActiveStatusEffects.size * (iconSize + iconSpacing)) / 2;

        // Update intensity text if changed
        if (existingSprite.lastIntensity !== effect.intensity) {
          this.updateStatusIntensityText(
            existingSprite,
            effect.intensity,
            iconSize,
            existingSprite.sprite?.zIndex ?? 9999,
          );
        }

        // Trigger bump effect only if intensity increased or duration increased (re-applied)
        // Duration naturally decreases each tick, so only bump if it INCREASED (refreshed)
        // AND not already bumping
        const intensityIncreased =
          effect.intensity > existingSprite.lastIntensity;
        const durationIncreased =
          effect.durationTicks > existingSprite.lastDuration;

        if (
          !existingSprite.isBumping &&
          (intensityIncreased || durationIncreased)
        ) {
          existingSprite.lastIntensity = effect.intensity;
          existingSprite.lastDuration = effect.durationTicks;
          existingSprite.isBumping = true;
          this.playBumpEffect(existingSprite.container, () => {
            existingSprite.isBumping = false;
          });
        } else {
          // Update tracked values even if not bumping
          existingSprite.lastIntensity = effect.intensity;
          existingSprite.lastDuration = effect.durationTicks;
        }
      } else {
        // Create new icon - reserve the spot immediately to prevent duplicates
        const iconContainer = new Container();

        // Create placeholder sprite data immediately to prevent race condition
        const placeholderSpriteData = {
          container: iconContainer,
          sprite: null as any, // Will be set after texture loads
          intensityText: null,
          startTime: Date.now(),
          lastIntensity: effect.intensity,
          lastDuration: effect.durationTicks,
          isBumping: true,
        };
        this.statusEffectSprites.set(status, placeholderSpriteData);

        // Load and create sprite icon
        const spritePath = this.getStatusEffectSpritePath(status);
        if (spritePath) {
          Assets.load<Texture>(spritePath).then((texture) => {
            // Double-check the status still exists (might have been removed while loading)
            if (!this.statusEffectSprites.has(status)) {
              iconContainer.destroy({ children: true });
              return;
            }

            const sprite = new Sprite(texture);
            sprite.width = iconSize;
            sprite.height = iconSize;
            sprite.anchor.set(0.5);
            sprite.x = iconSize / 2;
            sprite.y = iconSize / 2;
            sprite.zIndex = 10000;
            iconContainer.addChild(sprite);

            // Update the sprite reference in the stored data
            placeholderSpriteData.sprite = sprite;

            // Play bump effect on creation
            this.playBumpEffect(iconContainer, () => {
              placeholderSpriteData.isBumping = false;
            });

            // Add intensity text
            this.updateStatusIntensityText(
              placeholderSpriteData,
              effect.intensity,
              iconSize,
              sprite.zIndex,
            );
          });
        }

        iconContainer.x =
          xOffset -
          (this.ActiveStatusEffects.size * (iconSize + iconSpacing)) / 2;
        this.statusEffectContainer.addChild(iconContainer);
      }

      xOffset += iconSize + iconSpacing;
    }
  }

  /**
   * Update or create the intensity number text on the status icon
   */
  protected updateStatusIntensityText(
    spriteData: { container: Container; intensityText: Text | null },
    intensity: number,
    iconSize: number,
    iconZ: number,
  ) {
    if (intensity <= 1) {
      if (spriteData.intensityText) {
        spriteData.intensityText.visible = false;
      }
      return;
    }

    if (!spriteData.intensityText) {
      const intensityText = new Text({
        text: `${intensity}`,
        style: {
          fontFamily: "vck",
          fontSize: 13,
          fill: 0xffffff,
          align: "center",
          stroke: {
            color: 0x000000,
            width: 5,
          },
        },
      });
      // Position at bottom-right corner
      intensityText.anchor.set(1, 1);
      intensityText.x = iconSize * 1.25;
      intensityText.y = iconSize * 1.25;
      intensityText.zIndex = 1 + iconZ;

      spriteData.container.addChild(intensityText);
      spriteData.intensityText = intensityText;
    }

    spriteData.intensityText.text = `${intensity}`;
    spriteData.intensityText.visible = true;
  }

  /**
   * Play bump effect when status icon appears or gets re-applied
   * @param container - The container to animate
   * @param onComplete - Callback when animation completes
   */
  protected playBumpEffect(container: Container, onComplete?: () => void): void {
    const duration = 150; // Total animation duration in ms
    const startTime = Date.now();

    const animate = () => {
      if (!container || container.destroyed) {
        this.app.ticker.remove(animate);
        onComplete?.();
        return;
      }

      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Elastic ease-out effect - always animate from base scale 1.0
      let scale: number;
      if (progress < 0.33) {
        // Scale up to 1.4
        const t = progress / 0.33;
        scale = 1 + t * 0.4;
      } else if (progress < 0.66) {
        // Scale down to 0.9
        const t = (progress - 0.33) / 0.33;
        scale = 1.4 - t * 0.5;
      } else {
        // Return to normal (1.0)
        const t = (progress - 0.66) / 0.34;
        scale = 0.9 + t * 0.1;
      }

      container.scale.set(scale, scale);

      if (progress >= 1) {
        // Ensure we end at exactly scale 1.0
        container.scale.set(1, 1);
        this.app.ticker.remove(animate);
        onComplete?.();
      }
    };

    this.app.ticker.add(animate);
  }

  /**
   * Get the index of a direction in the DirectionArray (clockwise order)
   */
  protected getDirectionIndex(dir: Direction): number {
    const directions = [
      Direction.S,
      Direction.SE,
      Direction.E,
      Direction.NE,
      Direction.N,
      Direction.NW,
      Direction.W,
      Direction.SW,
    ];
    return directions.indexOf(dir);
  }

  /**
   * Calculate the shortest rotation path from one direction to another
   */
  protected getRotationPath(fromDir: Direction, toDir: Direction): Direction[] {
    const directions = [
      Direction.S,
      Direction.SE,
      Direction.E,
      Direction.NE,
      Direction.N,
      Direction.NW,
      Direction.W,
      Direction.SW,
    ];

    const fromIndex = this.getDirectionIndex(fromDir);
    const toIndex = this.getDirectionIndex(toDir);

    if (fromIndex === toIndex) return [];

    // Calculate both clockwise and counter-clockwise distances
    const clockwiseDistance = (toIndex - fromIndex + 8) % 8;
    const counterClockwiseDistance = (fromIndex - toIndex + 8) % 8;

    // Choose the shorter path
    const useClockwise = clockwiseDistance <= counterClockwiseDistance;
    const steps = useClockwise ? clockwiseDistance : counterClockwiseDistance;
    const stepDirection = useClockwise ? 1 : -1;

    const path: Direction[] = [];
    for (let i = 1; i <= steps; i++) {
      const nextIndex = (fromIndex + i * stepDirection + 8) % 8;
      path.push(directions[nextIndex] ?? this.currentDir);
    }

    return path;
  }

  /**
   * Start smooth rotation animation to target direction
   */
  protected startSmoothRotation(targetDir: Direction, onComplete: () => void) {
    if (this.isRotating) return;

    this.rotationPath = this.getRotationPath(this.currentDir, targetDir);
    if (this.rotationPath.length === 0) {
      onComplete();
      return;
    }

    this.isRotating = true;
    this.rotationStartTime = performance.now();
    this.rotationOnComplete = onComplete;

    const animate = () => {
      if (!this.isRotating || !this.animatedSprite) {
        this.app.ticker.remove(animate);
        return;
      }

      const elapsed = performance.now() - this.rotationStartTime;
      const progress = Math.min(elapsed / this.rotationDuration, 1);

      // Calculate which direction we should be at this progress
      const totalSteps = this.rotationPath.length;
      const currentStepIndex = Math.floor(progress * totalSteps);

      if (currentStepIndex < totalSteps) {
        const nextDir = this.rotationPath[currentStepIndex];
        if (nextDir !== this.currentDir) {
          // Update direction without changing animation
          this.currentDir = nextDir ?? this.currentDir;
          this.events.emit("animation_change", this.currentAnimName, nextDir);

          // Update sprite textures to the new direction
          const animSheet = this.animations[this.currentAnimName];
          if (animSheet) {
            const animFrames =
              animSheet.byDir[nextDir ?? this.currentDir] || [];
            this.animatedSprite.textures = animFrames.map((f) => f.texture);
            (this.animatedSprite as any)._durations = animFrames.map(
              (f) => f.time,
            );
          }

          // Update shadow sprite
          const shadowAnimSheet = this.shadowAnimations[this.currentAnimName];
          if (shadowAnimSheet && this.animatedShadowSprite) {
            const shadowFrames =
              shadowAnimSheet.byDir[nextDir ?? this.currentDir] || [];
            this.animatedShadowSprite.textures = shadowFrames.map(
              (f) => f.texture,
            );
            (this.animatedShadowSprite as any)._durations = shadowFrames.map(
              (f) => f.time,
            );
          }

          // Update premier aura
          this.updatePremierAuraTextures();
        }
      }

      if (progress >= 1) {
        // Rotation complete
        this.currentDir = targetDir;
        this.isRotating = false;
        this.app.ticker.remove(animate);

        if (this.rotationOnComplete) {
          this.rotationOnComplete();
          this.rotationOnComplete = null;
        }

        // Process any queued animation changes
        const pendingOnce = (this as any).pendingSetAnimationOnce;
        if (pendingOnce) {
          delete (this as any).pendingSetAnimationOnce;
          this.setAnimationOnceInternal(
            pendingOnce._name,
            pendingOnce.dir,
            pendingOnce.frame,
            pendingOnce.repetitions,
          );
        } else if (this.rotationQueue.length > 0) {
          const nextChange = this.rotationQueue.shift()!;
          this.setAnimationInternal(
            nextChange.animationOptions,
            nextChange.dir,
            nextChange.frame,
          );
        }
      }
    };

    this.app.ticker.add(animate);
  }

  /**
   * Get sprite path for status effect icon
   */
  protected getStatusEffectSpritePath(status: StatusEffect): string | null {
    switch (status) {
      case StatusEffect.BURN:
        return "/assets/status/BURN.png";
      case StatusEffect.POISON:
        return "/assets/status/POISON.png";
      case StatusEffect.STUN:
        return "/assets/status/STUN.png";
      case StatusEffect.SLEEP:
        return "/assets/status/SLEEP.png";
      case StatusEffect.SPEED_UP:
        return "/assets/status/SPEED_UP.png";
      case StatusEffect.SPEED_DOWN:
        return "/assets/status/SPEED_DOWN.png";
      case StatusEffect.ATTACK_UP:
        return "/assets/status/ATTACK_UP.png";
      case StatusEffect.ATTACK_DOWN:
        return "/assets/status/ATTACK_DOWN.png";
      case StatusEffect.DEFENSE_UP:
        return "/assets/status/DEFENSE_UP.png";
      case StatusEffect.DEFENSE_DOWN:
        return "/assets/status/DEFENSE_DOWN.png";
      case StatusEffect.FEAR:
        return "/assets/status/FEAR.png";
      case StatusEffect.CRITICAL_UP:
        // Fallback to ATTACK_UP if CRITICAL_UP doesn't have an icon
        return "/assets/status/ATTACK_UP.png";
      case StatusEffect.KNOCKUP:
        // Use STUN icon for knockup
        return "/assets/status/STUN.png";
      case StatusEffect.TETHERED:
        // Tethered uses a generic buff icon
        return "/assets/status/SPEED_UP.png";
      default:
        return null;
    }
  }

  /**
   * Override applyStatusEffect to handle knockup visual
   */
  public override applyStatusEffect(
    effect: SkillStatusEffect,
    sourceId?: number,
    currentTick?: number,
  ): void {
    super.applyStatusEffect(effect, sourceId ?? 0, currentTick ?? 0);

    // Handle knockup visual effect
    if (effect.status === StatusEffect.KNOCKUP) {
      this.startKnockupAnimation();
    }
  }

  /**
   * Override removeStatusEffect to handle knockup visual end
   */
  public override removeStatusEffect(status: StatusEffect): boolean {
    const result = super.removeStatusEffect(status);

    // Handle knockup visual end
    if (status === StatusEffect.KNOCKUP) {
      this.endKnockupAnimation();
    }

    return result;
  }

  /**
   * Start the knockup float animation
   */
  protected startKnockupAnimation(): void {
    if (this.isKnockedUp || !this.animatedSprite) return;

    this.isKnockedUp = true;
    this.knockupBaseY = this.animatedSprite.y;

    const floatHeight = 25; // pixels to float up
    const bounceSpeed = 1500; // ms per bounce cycle
    const startTime = performance.now();

    const animate = () => {
      if (!this.isKnockedUp || !this.animatedSprite) {
        this.app.ticker.remove(animate);
        return;
      }

      const elapsed = performance.now() - startTime;

      // Initial rise (first 200ms)
      if (elapsed < 200) {
        const t = elapsed / 200;
        const easeOut = 1 - Math.pow(1 - t, 3);
        this.animatedSprite.y = this.knockupBaseY - floatHeight * easeOut;
      } else {
        // Gentle bounce using sine wave
        const bounceElapsed = elapsed - 200;
        const bounce = Math.sin((bounceElapsed / bounceSpeed) * Math.PI * 2);
        this.animatedSprite.y = this.knockupBaseY - floatHeight + bounce * 5; // 5px bounce amplitude
      }
    };

    this.app.ticker.add(animate);
  }

  /**
   * End the knockup animation and return to ground
   */
  protected endKnockupAnimation(): void {
    if (!this.isKnockedUp || !this.animatedSprite) return;

    this.isKnockedUp = false;

    // Animate falling back down
    const currentY = this.animatedSprite.y;
    const startTime = performance.now();
    const fallDuration = 150; // ms

    const animateFall = () => {
      if (!this.animatedSprite) {
        this.app.ticker.remove(animateFall);
        return;
      }

      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / fallDuration);
      const easeIn = t * t; // Ease in for natural fall

      this.animatedSprite.y =
        currentY + (this.knockupBaseY - currentY) * easeIn;

      if (t >= 1) {
        this.animatedSprite.y = this.knockupBaseY;
        this.app.ticker.remove(animateFall);
      }
    };

    this.app.ticker.add(animateFall);
  }

  /** Update HP visuals. */
  public setHP(current: number) {
    if (!this) {
      console.warn("setHP called on destroyed pokemon");
      return;
    }

    // Update data
    this.hp.current = current;

    if (!this.hpBg || !this.hpFill) return;
    if (this.hpBg.destroyed || this.hpFill.destroyed) return;

    const ratio = Math.max(
      0,
      Math.min(1, this.hp.max > 0 ? current / this.hp.max : 0),
    );
    const fullWidth = this.hpBg.width * 0.7; // Magic string for now. TODO: fix this.
    const newWidth = Math.round(fullWidth * ratio);

    // Using a texture frame clone for the mask keeps pixels crisp at integer widths
    this.hpFill.width = newWidth;
  }

  /** Update shield bar visuals. */
  public setShield(current: number, max: number) {
    if (!this.shieldFill) return;
    if (this.shieldFill.destroyed) return;

    const hasShield = current > 0;
    this.shieldFill.visible = hasShield;

    if (hasShield) {
      const ratio = Math.max(0, Math.min(1, max > 0 ? current / max : 0));
      const fullWidth = this.hpBg.width * 0.7; // Magic string for now. TODO: fix this.
      const newWidth = Math.round(fullWidth * ratio);
      this.shieldFill.width = newWidth;
    }
  }

  // Call this when you swap animations elsewhere so we track the name
  public setAnimation(
    _animationOptions: string[],
    dir: Direction,
    frame?: number,
  ) {
    // If currently rotating, queue this animation change
    if (this.isRotating) {
      this.rotationQueue.push({
        animationOptions: _animationOptions,
        dir,
        frame: frame ?? 0,
      });
      return;
    }

    // If direction is different, start smooth rotation first
    if (dir !== this.currentDir) {
      this.startSmoothRotation(dir, () => {
        this.setAnimationInternal(_animationOptions, dir, frame);
      });
    } else {
      // Same direction, apply immediately
      this.setAnimationInternal(_animationOptions, dir, frame);
    }
  }

  protected setAnimationInternal(
    _animationOptions: string[],
    dir: Direction,
    frame?: number,
  ) {
    let name: string | null = null;
    let animationOptions = _animationOptions;
    const isDead = this.HP.current <= 0;

    if (isDead) {
      animationOptions = ["Sleep"];
    }

    for (const possibleAnim of animationOptions) {
      if (this.animations[possibleAnim]) {
        name = possibleAnim;
        break;
      }
    }
    if (
      !name ||
      !this.animatedSprite ||
      this.animatedSprite.destroyed ||
      !this.animatedShadowSprite ||
      this.animatedShadowSprite.destroyed
    ) {
      console.warn("Animation not found", animationOptions);
      return;
    }

    // If moving, force Walk animation instead of Idle
    if (this.isMoving && !isDead) {
      name = this.animations["Walk"] ? "Walk" : name;
      dir = this.movingDir;
    }
    // When going idle (and not moving), face current target or nearest enemy
    else if (name === "Idle") {
      dir = this.getTargetDirection() ?? dir;
    }

    if (name === this.currentAnimName && dir === this.currentDir) {
      // If its the same, check it its paused
      if (!this.animatedSprite.playing) this.animatedSprite.play();
      if (!this.animatedShadowSprite.playing) this.animatedShadowSprite.play();
      return;
    }

    const animSheet = this.animations[name]!;
    const shadowAnimSheet = this.shadowAnimations[name]!;

    this.currentAnimName = name;
    this.currentDir = dir;

    this.events.emit("animation_change", name, dir);

    // Update main sprite
    const animFrames = animSheet.byDir[dir] || [];
    this.animatedSprite.textures = animFrames.map((f) => f.texture);
    (this.animatedSprite as any)._durations = animFrames.map((f) => f.time);
    if (this.Name.toLowerCase() === "swampert")
      console.log(`[ClientPokemon] setAnimation`, name, dir);
    // Clamp frame to valid range for this animation
    const clampedFrame = Math.min(
      frame ?? 0,
      Math.max(0, animFrames.length - 1),
    );
    this.animatedSprite.gotoAndPlay(clampedFrame);

    // Update shadow sprite
    const shadowFrames = shadowAnimSheet.byDir[dir] || [];
    this.animatedShadowSprite.textures = shadowFrames.map((f) => f.texture);
    (this.animatedShadowSprite as any)._durations = shadowFrames.map(
      (f) => f.time,
    );
    const clampedShadowFrame = Math.min(
      frame ?? 0,
      Math.max(0, shadowFrames.length - 1),
    );
    this.animatedShadowSprite.gotoAndPlay(clampedShadowFrame);

    for (const auraSprite of this.premierAuraSprites) {
      auraSprite.textures = animFrames.map((f) => f.texture);
      (auraSprite as any)._durations = animFrames.map((f) => f.time);
      auraSprite.gotoAndPlay(clampedFrame);
    }

    // Update premier aura outline sprites to match animation
    this.updatePremierAuraTextures();

    this.layoutUI(false);
  }

  public getAvailableAnimations(): string[] {
    return Object.keys(this.animations);
  }

  public getSpecialMeleeAnimations(): string[] {
    return this.specialMeleeAnimations;
  }

  public getSpecialRangedAnimations(): string[] {
    return this.specialRangedAnimations;
  }

  // Play animation once without looping (for skill animations)
  public setAnimationOnce(
    _name: string | string[],
    dir: Direction,
    frame?: number,
    repetitions: number = 1,
  ) {
    // If currently rotating, queue this animation change
    if (this.isRotating) {
      // Store the original parameters for later
      (this as any).pendingSetAnimationOnce = {
        _name,
        dir,
        frame,
        repetitions,
      };
      return;
    }

    // If direction is different, start smooth rotation first
    if (dir !== this.currentDir) {
      this.startSmoothRotation(dir, () => {
        this.setAnimationOnceInternal(_name, dir, frame, repetitions);
      });
    } else {
      // Same direction, apply immediately
      this.setAnimationOnceInternal(_name, dir, frame, repetitions);
    }
  }

  protected setAnimationOnceInternal(
    _name: string | string[],
    dir: Direction,
    frame?: number,
    repetitions: number = 1,
  ) {
    let name: string | null = null;
    if (typeof _name === "string") {
      name = _name;
    } else {
      for (const possibleAnim of _name) {
        if (this.animations[possibleAnim]) {
          name = possibleAnim;
          break;
        }
      }
    }

    if (!name) return;
    if (this.isMoving && name == "Idle") return;

    const animSheet = this.animations[name];
    const shadowAnimSheet = this.shadowAnimations[name];

    if (
      !animSheet ||
      !shadowAnimSheet ||
      !this.animatedSprite ||
      this.animatedSprite.destroyed ||
      !this.animatedShadowSprite ||
      this.animatedShadowSprite.destroyed
    ) {
      console.warn("Animation not found", name);
      return;
    }

    this.currentAnimName = name;
    this.currentDir = dir;

    this.events.emit("animation_change", name, dir);

    const animFrames = animSheet.byDir[dir] || [];
    this.animatedSprite.textures = animFrames.map((f) => f.texture);
    (this.animatedSprite as any)._durations = animFrames.map((f) => f.time);

    // Update shadow sprite
    const shadowFrames = shadowAnimSheet.byDir[dir] || [];
    this.animatedShadowSprite.textures = shadowFrames.map((f) => f.texture);
    (this.animatedShadowSprite as any)._durations = shadowFrames.map(
      (f) => f.time,
    );

    // Play once without looping
    this.animatedSprite.loop = false;
    this.animatedShadowSprite.loop = false;
    this.animatedSprite.gotoAndPlay(frame ?? 0);
    this.animatedShadowSprite.gotoAndPlay(frame ?? 0);

    for (const auraSprite of this.premierAuraSprites) {
      auraSprite.textures = animFrames.map((f) => f.texture);
      (auraSprite as any)._durations = animFrames.map((f) => f.time);
      auraSprite.gotoAndPlay(frame ?? 0);
      auraSprite.loop = false;

      auraSprite.onComplete = () => {
        auraSprite.loop = true;

        delete (auraSprite as any).onComplete;
      };
    }

    // Return to Idle animation when skill animation completes
    this.animatedSprite.onComplete = () => {
      if (this.animatedSprite && !this.animatedSprite.destroyed) {
        if (repetitions > 1) {
          this.setAnimationOnceInternal(name!, dir, 0, repetitions - 1);
          return;
        }

        this.animatedSprite.loop = true;
        // Return to Idle after skill animation completes
        this.setAnimation(["Idle"], this.currentDir);
        delete (this.animatedSprite as any).onComplete;
      }
    };

    // Reset loop back to true, for shadow sprite
    this.animatedShadowSprite.onComplete = () => {
      if (this.animatedShadowSprite && !this.animatedShadowSprite.destroyed) {
        this.animatedShadowSprite.loop = true;
      }
      delete (this.animatedShadowSprite as any).onComplete;
    };

    // Update premier aura outline sprites to match animation
    this.updatePremierAuraTextures();

    this.layoutUI(false);
  }

  /**
   * Get base filters without the white flash filter.
   * This prevents capturing the white flash in concurrent damage events.
   */
  protected getBaseFilters(): Filter[] {
    if (!this.animatedSprite) return [];
    const currentFilters = this.animatedSprite.filters?.slice() ?? [];
    // Filter out the white flash to get the true base filters
    return currentFilters.filter((f) => f !== this.whiteFlash);
  }

  /**
   * Safety mechanism to restore sprite filters if stuck white.
   * Called after damage animation completes.
   */
  protected ensureFiltersRestored() {
    if (!this.animatedSprite || this.animatedSprite.destroyed) return;
    if (this.isFlashing) return; // Don't interfere with active flash

    // If whiteFlash is still in filters after animation, remove it
    const currentFilters = this.animatedSprite.filters ?? [];
    if (currentFilters.includes(this.whiteFlash)) {
      this.animatedSprite.filters = this.getBaseFilters();
    }
  }

  /**
   * Visual-only damage display. Does NOT modify HP.
   * HP is controlled by the server and synced via skill events.
   * @param amount - Damage amount
   * @param skipAnimation - If true, skip hurt animation (for DoT/subtle damage)
   */
  public override async takeDamage(
    rawAmount: number,
    skipAnimation: boolean = false,
  ): Promise<boolean> {
    if (!this.animatedSprite || this.animatedSprite.destroyed) return false;

    const amount = Math.round(rawAmount);

    // If damage is 0 (or less), treat as a miss/evade
    if (amount <= 0) {
      this.showFloatingDamage(0);
      return false;
    }

    // Allow DoT damage even if already hurting (just won't play animation)
    if (this.isHurting && !skipAnimation) return false;

    // Prevent concurrent flashes to avoid race conditions
    if (this.isFlashing) {
      // Still show damage and update state, just skip the flash
      const fatal = super.takeDamage(amount, skipAnimation);
      this.showFloatingDamage(amount);
      return fatal;
    }

    if (!skipAnimation) {
      this.isHurting = true;
      this.events.emit("hurt", true);
    }

    // Call parent takeDamage to update shield/HP state
    const fatal = super.takeDamage(amount, skipAnimation);

    // Always show floating damage
    this.showFloatingDamage(amount);

    const WHITE_MATRIX: ColorMatrix = [
      // R'
      0, 0, 0, 0, 1,
      // G'
      0, 0, 0, 0, 1,
      // B'
      0, 0, 0, 0, 1,
      // A'  (keep alpha)
      0, 0, 0, 1, 0,
    ];

    // Mark as flashing to prevent concurrent flashes
    this.isFlashing = true;

    try {
      // Get base filters (excluding any existing white flash)
      const baseFilters = this.getBaseFilters();

      // If skipAnimation is true (DoT damage), just show a quick flash
      if (skipAnimation) {
        this.whiteFlash.matrix = WHITE_MATRIX;
        this.animatedSprite!.filters = [...baseFilters, this.whiteFlash];
        await this.sleep(50);
        this.animatedSprite!.filters = baseFilters;
        return fatal;
      }

      // Store current animation
      const animationBeforeHurt = this.currentAnimName;
      const frameBeforeHurt = this.animatedSprite.currentFrame;

      // Check if currently in an attack animation
      const isAttacking =
        this.currentAnimName === "Attack" ||
        this.currentAnimName === "Charge" ||
        this.specialMeleeAnimations.includes(this.currentAnimName) ||
        this.specialRangedAnimations.includes(this.currentAnimName);

      // If attacking, only show white flash - don't interrupt the attack animation
      // if (isAttacking) {
      //   for (let i = 0; i < 2; i++) {
      //     this.whiteFlash.matrix = WHITE_MATRIX;
      //     this.animatedSprite!.filters = [...baseFilters, this.whiteFlash];
      //     await this.sleep(30);

      //     this.animatedSprite!.filters = baseFilters;
      //     await this.sleep(30);
      //   }

      //   this.isHurting = false;
      //   return;
      // }

      // Normal hurt behavior: play Hurt animation + flash
      if (this.animations["Hurt"]) {
        this.setAnimationOnce("Hurt", this.currentDir);
      }

      for (let i = 0; i < 2; i++) {
        this.whiteFlash.matrix = WHITE_MATRIX;
        this.animatedSprite!.filters = [...baseFilters, this.whiteFlash];
        await this.sleep(30);

        this.animatedSprite!.filters = baseFilters;
        await this.sleep(30);
      }

      const restoreAnim =
        animationBeforeHurt === "Hurt" ? "Idle" : animationBeforeHurt;
      this.setAnimation([restoreAnim], this.currentDir, frameBeforeHurt);
      this.isHurting = false;
      this.events.emit("hurt", false);
    } finally {
      // Always restore the flashing flag and ensure filters are correct
      this.isFlashing = false;
      // Safety check: ensure filters are properly restored
      this.ensureFiltersRestored();
    }

    return fatal;
  }

  /**
   * Sync shield visuals with current shield state.
   * Called after damage is taken to update shield bar.
   */
  public syncShield(currentShield: number) {
    this.shield.amount = Math.max(0, currentShield);
    this.setShield(this.shield.amount, this.maxShieldAmount);
  }

  /**
   * Override addShield to track max shield amount for bar scaling
   */
  public override addShield(amount: number, durationTicks: number) {
    super.addShield(amount, durationTicks);
    // Track the maximum shield amount for proper bar scaling
    this.maxShieldAmount = this.shield.amount;
  }

  // /**
  //  * Override tickShield to update visuals when shield expires
  //  */
  // public override tickShield() {
  //   const hadShield = this.shield.amount > 0;
  //   super.tickShield();

  //   // If shield expired, update visuals
  //   if (hadShield && this.shield.amount === 0) {
  //     this.syncShield();
  //   }
  // }

  public showFloatingDamage(amount: number, crit = false) {
    if (!this.animatedSprite || this.animatedSprite.destroyed) return;

    const isMiss = amount <= 0;
    const textContent = isMiss ? "MISS" : String(amount);

    // Base damage text style
    const color = isMiss ? 0xffffff : crit ? 0xffff33 : 0xff3333;

    // Outer stroke (black)
    const outer = new Text({
      text: textContent,
      style: {
        letterSpacing: 1,
        fontFamily: "vck",
        fontSize: isMiss ? 12 : crit ? 20 : 15,
        fill: color,
        stroke: { color: 0x000000, width: 8 },
      },
    });
    outer.anchor.set(0, 0);

    // Inner stroke (white, thinner)
    const inner = new Text({
      text: textContent,
      style: {
        letterSpacing: 1,
        fontFamily: "vck",
        fontSize: isMiss ? 12 : crit ? 20 : 15,
        fill: color,
        stroke: { color: 0xffffff, width: 3 },
      },
    });
    inner.anchor.set(0, 0);

    // Container for both layers
    const dmgContainer = new Container();
    dmgContainer.addChild(outer, inner);

    dmgContainer.x = this.animatedSprite.x;
    dmgContainer.y = this.animatedSprite.y - this.animatedSprite.height / 2 - 4;

    this.container.addChild(dmgContainer);

    // Animate upwards + fade
    const startY = dmgContainer.y;
    const endY = startY - 30;
    const duration = 1500;
    const startTime = performance.now();

    const tick = () => {
      if (!dmgContainer || dmgContainer.destroyed) {
        this.app.ticker.remove(tick);
        return;
      }

      const now = performance.now();
      const t = Math.min(1, (now - startTime) / duration);
      dmgContainer.y = startY + (endY - startY) * t;
      dmgContainer.alpha = 1 - t;

      if (t >= 1) {
        this.app.ticker.remove(tick);
        dmgContainer.destroy();
      }
    };
    this.app.ticker.add(tick);
  }

  public showFloatingHeal(amount: number) {
    if (!this.animatedSprite) return;

    // Green color for healing
    const color = 0x33ff33;

    // Outer stroke (black)
    const outer = new Text({
      text: `+${amount}`,
      style: {
        letterSpacing: 1,
        fontFamily: "vck",
        fontSize: 16,
        fill: color,
        stroke: { color: 0x000000, width: 8 },
      },
    });
    outer.anchor.set(0, 0);

    // Inner stroke (white, thinner)
    const inner = new Text({
      text: `+${amount}`,
      style: {
        letterSpacing: 1,
        fontFamily: "vck",
        fontSize: 16,
        fill: color,
        stroke: { color: 0xffffff, width: 3 },
      },
    });
    inner.anchor.set(0, 0);

    // Container for both layers
    const healContainer = new Container();
    healContainer.addChild(outer, inner);

    healContainer.x = this.animatedSprite.x;
    healContainer.y =
      this.animatedSprite.y - this.animatedSprite.height / 2 - 4;

    this.container.addChild(healContainer);

    // Animate upwards + fade
    const startY = healContainer.y;
    const endY = startY - 30;
    const duration = 1500;
    const startTime = performance.now();

    const tick = () => {
      if (!healContainer || healContainer.destroyed) {
        this.app.ticker.remove(tick);
        return;
      }

      const now = performance.now();
      const t = Math.min(1, (now - startTime) / duration);
      healContainer.y = startY + (endY - startY) * t;
      healContainer.alpha = 1 - t;

      if (t >= 1) {
        this.app.ticker.remove(tick);
        healContainer.destroy();
      }
    };
    this.app.ticker.add(tick);
  }

  // --- TURN executor ---
  public async applyTurn(action: Battle_Action_Turn) {
    if (!this.animatedSprite) return;

    const toDir = action.to;

    this.setAnimation([this.currentAnimName], toDir);
  }

  // --- MOVE executor ---
  public async applyMove(action: Battle_Action_Move) {
    if (!this.animatedSprite || this.isHurting) return;

    if (this.Name.toLowerCase() === "swampert")
      console.log(`[ClientPokemon] applyMove`, action);

    const fromPos = this.Position;

    // Create a copy to avoid mutating the original action object
    const toPos = {
      x: action.to.x * gridTileSize + gridMarginX + gridTileSize / 2,
      y: action.to.y * gridTileSize + gridMarginY + gridTileSize / 2,
    };

    const dx = toPos.x - fromPos.x;
    const dy = toPos.y - fromPos.y;
    const distZero = dx === 0 && dy === 0;

    const dir = distZero ? this.currentDir : vectorToDir(dx, dy);

    // Set moving state
    this.ongoingMovementActions++;
    this.movingDir = dir;

    // play Walk if available
    if (this.animations["Walk"]) this.setAnimation(["Walk"], dir);

    const totalMs = Math.max(1, action.total_ticks * MS_PER_TICK);
    const startX = this.container.x,
      startY = this.container.y;
    const endX = toPos.x,
      endY = toPos.y;
    const startTime = performance.now();

    let activeTime = 0; // Track only the time when NOT paused

    await new Promise<void>((resolve) => {
      const step = () => {
        const now = performance.now();
        // Pause movement if Pokemon is hurting
        if (this.isHurting) {
          return;
        }

        // Add elapsed time since last active frame
        const deltaMS = this.app.ticker.deltaMS;
        activeTime += deltaMS;

        const t = Math.min(1, activeTime / totalMs);

        // linear lerp (snap to ints for pixel-crisp)
        this.container.x = Math.round(startX + (endX - startX) * t);
        this.container.y = Math.round(startY + (endY - startY) * t);
        this.container.zIndex = Math.round(startY + (endY - startY) * t);
        this.Position.x = Math.round(startX + (endX - startX) * t);
        this.Position.y = Math.round(startY + (endY - startY) * t);

        if (t >= 1) {
          this.container.x = endX;
          this.container.y = endY;
          this.container.zIndex = endY;
          this.Position.x = endX;
          this.Position.y = endY;
          this.app.ticker.remove(step);
          resolve();
        }
      };

      this.app.ticker.add(step);
    });

    // Clear moving state
    this.ongoingMovementActions--;

    // back to Idle (same facing) if available, but only if not playing a skill animation
    const isPlayingSkillAnim =
      this.specialMeleeAnimations.includes(this.currentAnimName) ||
      this.specialRangedAnimations.includes(this.currentAnimName);
    if (this.animations["Idle"] && !isPlayingSkillAnim) {
      this.setAnimation(["Idle"], dir);
    }

    if (this.Name.toLowerCase() === "swampert")
      console.log(`[ClientPokemon] applyMove end`);
  }

  // --- DASH executor (gap close with speed lines) ---
  public async applyDash(toPos: Position, durationMs: number = 80) {
    if (!this.animatedSprite) return;

    const startX = this.container.x;
    const startY = this.container.y;
    const endX = toPos.x;
    const endY = toPos.y;

    const dx = endX - startX;
    const dy = endY - startY;
    if (dx === 0 && dy === 0) return;

    const dir = vectorToDir(dx, dy);
    this.setAnimation([this.SpecialMeleeAnimation], dir);

    // Create speed lines
    const speedLines = this.createSpeedLines(startX, startY, endX, endY);

    const startTime = performance.now();

    await new Promise<void>((resolve) => {
      const step = () => {
        const now = performance.now();
        const elapsed = now - startTime;
        // Use ease-out for snappy dash feel
        const t = Math.min(1, elapsed / durationMs);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic

        this.container.x = Math.round(startX + dx * eased);
        this.container.y = Math.round(startY + dy * eased);
        this.container.zIndex = this.container.y;
        this.Position.x = this.container.x;
        this.Position.y = this.container.y;

        if (t >= 1) {
          this.container.x = endX;
          this.container.y = endY;
          this.container.zIndex = endY;
          this.Position.x = endX;
          this.Position.y = endY;
          this.app.ticker.remove(step);
          resolve();
        }
      };
      this.app.ticker.add(step);
    });

    // Speed lines fade out independently (don't await)
    this.fadeOutSpeedLines(speedLines, 120);
  }

  // Creates procedural speed lines from start to end position
  protected createSpeedLines(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
  ): Graphics[] {
    const lines: Graphics[] = [];
    const lineCount = 6;
    const dx = endX - startX;
    const dy = endY - startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Perpendicular offset direction
    const perpX = -Math.sin(angle);
    const perpY = Math.cos(angle);

    for (let i = 0; i < lineCount; i++) {
      const g = new Graphics();

      // Offset each line perpendicular to movement direction
      const spread = 12;
      const offset = (i - (lineCount - 1) / 2) * spread;
      const offsetX = perpX * offset;
      const offsetY = perpY * offset;

      // Line starts from origin area, ends partway toward destination
      const lineLength = distance * (0.4 + Math.random() * 0.3);
      const lineStartX = startX + offsetX + dx * 0.1;
      const lineStartY = startY + offsetY + dy * 0.1;
      const lineEndX = lineStartX + (dx / distance) * lineLength;
      const lineEndY = lineStartY + (dy / distance) * lineLength;

      // Draw tapered line (thicker at start, thinner at end)
      const thickness = 4 + Math.random() * 4;
      g.moveTo(lineStartX, lineStartY);
      g.lineTo(lineEndX, lineEndY);
      g.stroke({ width: thickness, color: 0xffffff, alpha: 0.7 });

      g.zIndex = startY - 1;
      this.parent.addChild(g);
      lines.push(g);
    }

    return lines;
  }

  // Fade out and destroy speed lines
  protected fadeOutSpeedLines(lines: Graphics[], durationMs: number) {
    const startTime = performance.now();

    const fade = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / durationMs);

      for (const line of lines) {
        line.alpha = 1 - t;
      }

      if (t >= 1) {
        this.app.ticker.remove(fade);
        for (const line of lines) {
          line.destroy();
        }
      }
    };
    this.app.ticker.add(fade);
  }

  // --- DANCE executor ---
  // 1. Get what types of animation are available for this pokemon:
  // 2. Pick Dance or Shake. Pick Hop.
  // 3. Alternate between Dance/Shake and Hop, for the total of action.total_ticks
  // DISABLED FOR NOW
  public async applyDance(action: Battle_Action_Dance) {
    if (!this.animatedSprite) return;
    if (this.isMoving) return;

    // Only set Idle if not playing a skill animation
    const isPlayingSkillAnimStart =
      this.specialMeleeAnimations.includes(this.currentAnimName) ||
      this.specialRangedAnimations.includes(this.currentAnimName);
    if (!isPlayingSkillAnimStart) {
      this.setAnimation(["Idle"], this.currentDir);
    }

    await this.sleep(action.total_ticks * MS_PER_TICK);

    // Only set Idle if not playing a skill animation
    const isPlayingSkillAnimEnd =
      this.specialMeleeAnimations.includes(this.currentAnimName) ||
      this.specialRangedAnimations.includes(this.currentAnimName);
    if (!isPlayingSkillAnimEnd) {
      this.setAnimation(["Idle"], this.currentDir);
    }
  }

  // --- FAINT executor ---
  // 1. Get what types of animation are available for this pokemon = Faint > Sleep > Idle
  // 2. Play the highest priority available animation
  // 3. Sleep for the total of action.total_ticks
  // 4. Fade out sprite
  // 5. Destroy pokemon and clean up.
  public async applyFaint(action: Battle_Action_Faint) {
    if (!this.animatedSprite) return;

    // Stop premier aura animation first
    this.stopPremierAura();
    this.stopVisualStatusAnimation();

    // Stop any ongoing animations
    if (this.animatedSprite) {
      this.animatedSprite.stop();
    }

    const vfxPath = "/assets/spells/impact/fanfx1_skull_puff_large_white.png";
    const vfx = new VFX({
      app: this.app,
      parent: this.parent,
      spriteUrl: vfxPath,
      spriteFrameCount: VFX_SPRITE_FRAME_COUNT_DATA[vfxPath] ?? 12,
      spriteScale: 1.5,
      spriteScaleX: 1.5,
      spriteScaleY: 1.5,
      spriteAlpha: 1,
      ping_pong: false,
      position: { x: this.Position.x, y: this.Position.y },
      loops: 1,
      speed: 0.35,
    });

    await vfx.load();
    void vfx.play();

    this.events.emit("faint");

    // Set animation to Sleep/Faint (Face South to ensure visibility)
    this.setAnimation(["Sleep"], Direction.S);

    this.animatedSprite.alpha = 0.4;
    if (this.animatedShadowSprite) this.animatedShadowSprite.alpha = 0.4;

    // Apply grayscale filter to the sprites
    const grayFilter = new ColorMatrixFilter();
    grayFilter.desaturate();

    if (this.animatedSprite) {
      this.animatedSprite.filters = [grayFilter];
    }
    if (this.animatedShadowSprite) {
      this.animatedShadowSprite.filters = [grayFilter];
    }

    // Hide UI elements
    if (this.nameText) this.nameText.visible = false;
    if (this.hpBg) this.hpBg.visible = false;
    if (this.hpFill) this.hpFill.visible = false;
    if (this.shieldFill) this.shieldFill.visible = false;
    if (this.statusEffectContainer) this.statusEffectContainer.visible = false;

    // Hide Visual Status elements
    if (this.visualStatusGlow) this.visualStatusGlow.visible = false;
    if (this.visualStatusText) this.visualStatusText.visible = false;
    if (this.visualStatusArrow) this.visualStatusArrow.visible = false;

    // We do NOT destroy the container so the body remains visible
  }

  // --- USE SKILL executor ---
  public async applyUseSkill(action: Battle_Action_UseSkill) {
    if (!this.animatedSprite) return;

    const usedSkill = this.skills.find((skill) => skill.id === action.skill.id);
    if (!usedSkill)
      throw new Error("Used skill not found => id: " + action.skill.id);

    // Check if this is a healing skill
    const isHealingSkill = action.skill.types.includes(SkillType.HEAL);

    let targetPokemon: Pokemon | null = null;
    let targetDir = this.currentDir;

    if (isHealingSkill) {
      // For healing skills, target can be self or ally
      if (action.target_id === this.id || !action.target_id) {
        // Healing self or no specific target (area/all heals)
        targetPokemon = this;
      } else {
        // Healing a specific ally
        targetPokemon = this.getAllyPokemon(action.target_id);
      }

      if (targetPokemon && targetPokemon !== this) {
        targetDir = vectorToDir(
          targetPokemon.Position.x - this.Position.x,
          targetPokemon.Position.y - this.Position.y,
        );
        this.turnToFaceTarget(targetPokemon);
      }
    } else {
      // For offensive skills, target is enemy
      targetPokemon = this.getEnemyPokemon(action.target_id ?? 0);
      if (!targetPokemon) {
        targetPokemon = this.getAllyPokemon(action.target_id ?? 0);
      }

      if (!targetPokemon)
        throw new Error(
          `${this.id} cant find target => id: ${action.target_id}`,
        );

      targetDir = vectorToDir(
        targetPokemon.Position.x - this.Position.x,
        targetPokemon.Position.y - this.Position.y,
      );

      this.currentTargetId = action.target_id ?? null;
      this.turnToFaceTarget(targetPokemon);
    }

    await usedSkill.useSkill(action.skill, action.starting_tick, targetPokemon);

    // Wait for skill animation to complete before returning to idle
    await this.sleep(action.total_ticks * MS_PER_TICK);

    // Only reset to Idle if not playing a skill animation (prevents race conditions with concurrent skills)
    const isPlayingSkillAnim =
      this.specialMeleeAnimations.includes(this.currentAnimName) ||
      this.specialRangedAnimations.includes(this.currentAnimName);
    if (!isPlayingSkillAnim) {
      this.setAnimation(["Idle"], targetDir);
    }
  }

  public turnToFaceTarget = (enemy: Pokemon) => {
    const dx = enemy.Position.x - this.Position.x;
    const dy = enemy.Position.y - this.Position.y;
    const distZero = dx === 0 && dy === 0;

    const dir = distZero ? this.direction : vectorToDir(dx, dy);

    this.direction = dir;
  };

  /**
   * Get direction to face current target or nearest enemy.
   * Returns null if no valid target found.
   */
  private getTargetDirection(): Direction | null {
    // Try current target first
    if (this.currentTargetId !== null) {
      const target = this.getEnemyPokemon(
        this.currentTargetId,
      ) as ClientPokemon | null;
      if (target && !target.IsDestroyed && target.hp.current > 0) {
        const dx = target.Position.x - this.Position.x;
        const dy = target.Position.y - this.Position.y;
        if (dx !== 0 || dy !== 0) {
          return vectorToDir(dx, dy);
        }
      }
    }

    // Fall back to nearest enemy
    const nearest = this.findNearestEnemy();
    if (nearest) {
      const dx = nearest.Position.x - this.Position.x;
      const dy = nearest.Position.y - this.Position.y;
      if (dx !== 0 || dy !== 0) {
        return vectorToDir(dx, dy);
      }
    }

    return null;
  }

  /**
   * Find the nearest living enemy by pixel distance.
   */
  private findNearestEnemy(): ClientPokemon | null {
    let nearest: ClientPokemon | null = null;
    let minDist = Infinity;

    for (const enemy of this.enemies) {
      if (enemy.IsDestroyed || enemy.hp.current <= 0) continue;

      const dx = enemy.Position.x - this.Position.x;
      const dy = enemy.Position.y - this.Position.y;
      const dist = dx * dx + dy * dy;

      if (dist < minDist) {
        minDist = dist;
        nearest = enemy;
      }
    }

    return nearest;
  }

  // Getters
  public get CurrentDir(): Direction {
    return this.currentDir;
  }

  public get CurrentAnimName(): string {
    return this.currentAnimName;
  }

  public get Name(): string {
    return this.name ?? this.base_pokemon_name;
  }

  public get TargetId(): number | null {
    return this.currentTargetId;
  }

  public get GridPosition(): Position {
    return {
      x: Math.round(this.Position.x / gridTileSize),
      y: Math.round(this.Position.y / gridTileSize),
    };
  }

  public get SpecialMeleeAnimation(): string {
    let animation =
      this.specialMeleeAnimations[
      Math.floor(Math.random() * this.specialMeleeAnimations.length)
      ];

    if (animation) return animation;

    return "Attack";
  }

  public get SpecialRangedAnimation(): string {
    let animation =
      this.specialRangedAnimations[
      Math.floor(Math.random() * this.specialRangedAnimations.length)
      ];

    if (animation) return animation;

    return "Attack";
  }

  public get Team(): "A" | "B" {
    return this.team;
  }

  public override get Position(): Position {
    return this.container.position;
  }

  public get IsDestroyed(): boolean {
    return this.container.destroyed;
  }

  public override get Enemies(): ClientPokemon[] {
    return this.enemies;
  }

  /**
   * Start the premier aura effect with flickering colored outline.
   * Colors, speed, and stroke width can be configured via instance properties.
   */
  protected startPremierAura() {
    if (!this.animatedSprite) return;

    // Create outline sprites by offsetting in 8 directions
    const offsets = [
      { x: -1, y: 0 },
      { x: 1, y: 0 },
      { x: 0, y: -1 },
      { x: 0, y: 1 },
      { x: -1, y: -1 },
      { x: 1, y: -1 },
      { x: -1, y: 1 },
      { x: 1, y: 1 },
    ];

    // Get initial color
    const initialColor = this.premierAuraColors[0] ?? 0xffffff;
    const r = ((initialColor >> 16) & 0xff) / 255;
    const g = ((initialColor >> 8) & 0xff) / 255;
    const b = (initialColor & 0xff) / 255;

    // Create outline sprites
    for (const offset of offsets) {
      const outlineSprite = new AnimatedSprite(this.animatedSprite.textures);
      outlineSprite.anchor.set(0.5);
      outlineSprite.scale = this.animatedSprite.scale;
      outlineSprite.x = offset.x * this.premierAuraStrokeWidth;
      outlineSprite.y = offset.y * this.premierAuraStrokeWidth;
      outlineSprite.animationSpeed = ANIMATION_SPEED;

      // Create color matrix filter to make sprite solid color
      const colorFilter = new ColorMatrixFilter();
      const colorMatrix: ColorMatrix = [
        // R' = r
        0,
        0,
        0,
        0,
        r,
        // G' = g
        0,
        0,
        0,
        0,
        g,
        // B' = b
        0,
        0,
        0,
        0,
        b,
        // A' = original alpha (keep transparency)
        0,
        0,
        0,
        1,
        0,
      ];
      colorFilter.matrix = colorMatrix;
      outlineSprite.filters = [colorFilter];

      // Add outline sprite behind the main sprite
      const spriteIndex = this.container.getChildIndex(this.animatedSprite);
      this.container.addChildAt(outlineSprite, spriteIndex);
      this.premierAuraSprites.push(outlineSprite);
      this.premierAuraFilters.push(colorFilter);
    }

    // Start color flicker animation
    let colorIndex = 0;
    let lastTime = performance.now();

    const animate = () => {
      if (this.premierAuraSprites.length === 0 || !this.animatedSprite) {
        this.app.ticker.remove(animate);
        return;
      }

      const currentTime = performance.now();

      // Check if enough time has passed to switch colors
      if (currentTime - lastTime >= this.premierAuraSpeed) {
        colorIndex = (colorIndex + 1) % this.premierAuraColors.length;
        const color = this.premierAuraColors[colorIndex]!;

        // Convert hex to RGB (0-1 range)
        const r = ((color >> 16) & 0xff) / 255;
        const g = ((color >> 8) & 0xff) / 255;
        const b = (color & 0xff) / 255;

        // Update all outline sprites with new color matrix
        for (const filter of this.premierAuraFilters) {
          const colorMatrix: ColorMatrix = [
            // R' = r
            0,
            0,
            0,
            0,
            r,
            // G' = g
            0,
            0,
            0,
            0,
            g,
            // B' = b
            0,
            0,
            0,
            0,
            b,
            // A' = original alpha
            0,
            0,
            0,
            1,
            0,
          ];
          filter.matrix = colorMatrix;
        }

        lastTime = currentTime;
      }
    };

    this.app.ticker.add(animate);
  }

  /**
   * Stop the premier aura effect and clean up animation.
   */
  protected stopPremierAura() {
    // Destroy and remove all outline sprites and filters
    for (const sprite of this.premierAuraSprites) {
      if (!sprite.destroyed) sprite.destroy();
    }
    this.premierAuraSprites = [];
    this.premierAuraFilters = [];
  }

  /**
   * Update premier aura outline sprites to match the current main sprite texture.
   * Called whenever the animation changes.
   */
  protected updatePremierAuraTextures() {
    if (this.premierAuraSprites.length === 0 || !this.animatedSprite) return;

    const currentTexture = this.animatedSprite.texture;
    for (const sprite of this.premierAuraSprites) {
      sprite.texture = currentTexture;
    }
  }

  /**
   * Configure premier aura parameters.
   * Should be called before load() or after startPremierAura().
   */
  public setPremierAuraConfig(
    colors: number[],
    speed: number,
    strokeWidth: number,
  ) {
    this.premierAuraColors = colors;
    this.premierAuraSpeed = speed;
    this.premierAuraStrokeWidth = strokeWidth;

    // If aura is already running, restart with new config
    if (this.premierAuraSprites.length > 0) {
      this.stopPremierAura();
      this.startPremierAura();
    }
  }

  public getAnimationHitDelay(animName: string): number | null {
    const anim = this.animations[animName];
    if (!anim || !anim.meta || anim.meta.hitFrame === undefined) return null;

    const hitFrame = anim.meta.hitFrame;
    const frames = anim.byDir[Direction.SE] || Object.values(anim.byDir)[0];
    if (!frames) return null;

    let delay = 0;
    for (let i = 0; i < hitFrame; i++) {
      const frame = frames[i];
      if (frame) {
        delay += frame.time;
      }
    }

    return delay;
  }

  /**
   * Play a tail swing animation - rotates the pokemon through directions
   * to simulate a tail whip motion.
   *
   * @param phase - "in" for the fast swing to hit, "out" for the slower recovery
   * @param degrees - How many degrees to rotate (180 or 360)
   * @param durationMs - Total duration of the swing phase
   * @returns Promise that resolves when animation completes
   */
  public async playTailSwingAnimation(
    phase: "in" | "out",
    degrees: 180 | 360 = 180,
    durationMs?: number,
  ): Promise<void> {
    if (!this.animatedSprite || this.animatedSprite.destroyed) return;

    const directions = [
      Direction.S,
      Direction.SE,
      Direction.E,
      Direction.NE,
      Direction.N,
      Direction.NW,
      Direction.W,
      Direction.SW,
    ];

    const currentIndex = directions.indexOf(this.currentDir);
    if (currentIndex === -1) return;

    // Calculate how many direction steps to rotate
    // 180° = 4 steps, 360° = 8 steps
    const totalSteps = degrees === 180 ? 4 : 8;

    // Build the rotation path (clockwise)
    const rotationPath: Direction[] = [];
    for (let i = 1; i <= totalSteps; i++) {
      const nextIndex = (currentIndex + i) % 8;
      rotationPath.push(directions[nextIndex]!);
    }

    // For "out" phase, reverse the path (go back the way we came)
    if (phase === "out") {
      rotationPath.reverse();
    }

    // Default durations: "in" is fast (100ms), "out" is slower (180ms)
    const defaultDuration = phase === "in" ? 100 : 180;
    const duration = durationMs ?? defaultDuration;

    const startTime = performance.now();
    let lastStepIndex = -1;

    return new Promise<void>((resolve) => {
      const animate = () => {
        if (!this.animatedSprite || this.animatedSprite.destroyed) {
          this.app.ticker.remove(animate);
          resolve();
          return;
        }

        const elapsed = performance.now() - startTime;
        let progress = Math.min(1, elapsed / duration);

        // Apply easing based on phase
        if (phase === "in") {
          // Ease-in: starts slow, accelerates (feels like winding up then snapping)
          progress = progress * progress;
        } else {
          // Ease-out: starts fast, decelerates (recovery slowdown)
          progress = 1 - Math.pow(1 - progress, 2);
        }

        // Calculate which step we should be at
        const stepIndex = Math.min(
          Math.floor(progress * rotationPath.length),
          rotationPath.length - 1,
        );

        // Only update if we've moved to a new step
        if (stepIndex !== lastStepIndex && stepIndex < rotationPath.length) {
          lastStepIndex = stepIndex;
          const newDir = rotationPath[stepIndex]!;

          // Update direction and sprite textures without changing animation
          this.currentDir = newDir;

          // Update sprite textures to the new direction
          const animSheet = this.animations[this.currentAnimName];
          if (animSheet && this.animatedSprite) {
            const animFrames = animSheet.byDir[newDir] || [];
            if (animFrames.length > 0) {
              const currentFrame = this.animatedSprite.currentFrame;
              this.animatedSprite.textures = animFrames.map((f) => f.texture);
              (this.animatedSprite as any)._durations = animFrames.map(
                (f) => f.time,
              );
              // Preserve current frame position
              const clampedFrame = Math.min(
                currentFrame,
                animFrames.length - 1,
              );
              this.animatedSprite.gotoAndPlay(clampedFrame);
            }
          }

          // Update shadow sprite
          const shadowAnimSheet = this.shadowAnimations[this.currentAnimName];
          if (shadowAnimSheet && this.animatedShadowSprite) {
            const shadowFrames = shadowAnimSheet.byDir[newDir] || [];
            if (shadowFrames.length > 0) {
              const currentFrame = this.animatedShadowSprite.currentFrame;
              this.animatedShadowSprite.textures = shadowFrames.map(
                (f) => f.texture,
              );
              (this.animatedShadowSprite as any)._durations = shadowFrames.map(
                (f) => f.time,
              );
              const clampedFrame = Math.min(
                currentFrame,
                shadowFrames.length - 1,
              );
              this.animatedShadowSprite.gotoAndPlay(clampedFrame);
            }
          }

          // Update premier aura if active
          this.updatePremierAuraTextures();
        }

        if (elapsed >= duration) {
          this.app.ticker.remove(animate);
          resolve();
        }
      };

      this.app.ticker.add(animate);
    });
  }
}
